import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  ArrowRight as ArrowRightIcon,
  Workflow as FlowArrowIcon
} from 'lucide-react'
import {
  useGetPlantProcessDetailQuery,
  usePlantSessionPaint,
  collectAdditionalProperties,
  type PlantProcess,
  type PlantProcessImplementation
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { PlantHref } from './PlantHref'
import {
  SessionEntityMark,
  SessionGhostTile,
  SessionMarkAffordances,
  SessionTitleMark,
  sessionDetailHeaderClass,
  sessionMarkClass,
  useVisibleGhosts
} from './session-mark-ui'
import { UnorderedNotice } from './UnorderedNotice'

/**
 * Right-pane detail body for a selected process. Owns the detail query
 * keyed on `processId` and renders loading / error / body states.
 *
 * Composition contract: the page owns the breadcrumb, the two-column
 * grid wrapper, and the left-rail tree; this panel only fills the right
 * column. Body sections:
 *   - Header: optional `↑ <parent>` button, title, description, chips
 *     (subprocesses, implementations, sites)
 *   - Inputs / Outputs card (free-form ports declared on the Process node)
 *   - Subprocess flow (clickable child nodes)
 *   - Per-site implementation cards (clickable lines / sublines, jumping
 *     into the Equipment tab via the shared Plant entity link)
 */
export interface ProcessDetailPanelProps {
  processId: string
}

export function ProcessDetailPanel({ processId }: ProcessDetailPanelProps): React.ReactElement {
  const detail = useGetPlantProcessDetailQuery(processId, {
    skip: !processId
  })

  const sites = React.useMemo(
    () => collectSites(detail.data?.implementations ?? []),
    [detail.data?.implementations]
  )

  if (detail.isError) {
    return <DetailErrorState />
  }
  if (detail.isLoading || !detail.data) {
    return <DetailLoadingState />
  }
  return (
    <ProcessDetailBody
      process={detail.data.process}
      isStray={detail.data.isStray}
      parent={detail.data.parent}
      subprocesses={detail.data.subprocesses}
      implementations={detail.data.implementations}
      siteCount={sites.length}
    />
  )
}

function DetailLoadingState(): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      <div className="text-sm text-ink-3">Loading process…</div>
    </div>
  )
}

function DetailErrorState(): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      <Empty
        title="Couldn't load this process"
        hint="The process may have been deleted, or the proxy is unreachable."
        icon={WarningCircleIcon}
      />
    </div>
  )
}

interface ProcessDetailBodyProps {
  process: PlantProcess
  isStray: boolean
  parent: PlantProcess | null
  subprocesses: PlantProcess[]
  implementations: PlantProcessImplementation[]
  siteCount: number
}

function ProcessDetailBody({
  process,
  isStray,
  parent,
  subprocesses,
  implementations,
  siteCount
}: ProcessDetailBodyProps): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const processMark = markFor(process.id)
  const inputs = React.useMemo(() => collectPorts(process.inputs), [process.inputs])
  const outputs = React.useMemo(() => collectPorts(process.outputs), [process.outputs])
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('Process', process as unknown as Record<string, unknown>),
    [process]
  )

  return (
    <>
      <header className={sessionDetailHeaderClass('flex flex-col gap-2', processMark)}>
        <div className="text-xs text-ink-3">
          {parent ? (
            <PlantHref
              id={parent.id}
              label="Process"
              node={parent}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-ink-2 no-underline hover:bg-accent hover:text-accent-foreground"
            >
              <span aria-hidden>↑</span>
              <span>{parent.name}</span>
              <SessionEntityMark entityId={parent.id} />
            </PlantHref>
          ) : (
            <span className="text-11 uppercase tracking-[0.04em]">Root process</span>
          )}
        </div>
        <div className="flex flex-wrap items-baseline gap-2">
          <h1 className="m-0 text-2xl font-semibold text-ink">{process.name}</h1>
          <SessionTitleMark mark={processMark} />
        </div>
        {process.description && (
          <p className="m-0 max-w-3xl text-sm text-ink-3">{process.description}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline">
            {subprocesses.length} {subprocesses.length === 1 ? 'subprocess' : 'subprocesses'}
          </Badge>
          <Badge variant="outline">
            {implementations.length}{' '}
            {implementations.length === 1 ? 'implementation' : 'implementations'}
          </Badge>
          <Badge variant="outline">
            {siteCount} {siteCount === 1 ? 'site' : 'sites'}
          </Badge>
          <PlantStrayPill isStray={isStray} />
        </div>
      </header>

      <InputsOutputsCard inputs={inputs} outputs={outputs} />

      <SubprocessFlow processId={process.id} subprocesses={subprocesses} />

      <ImplementationsGrid processId={process.id} implementations={implementations} />

      <EntityDetailsInspector entity={process} additionalProperties={additionalProperties} />
    </>
  )
}

function InputsOutputsCard({
  inputs,
  outputs
}: {
  inputs: string[]
  outputs: string[]
}): React.ReactElement | null {
  const hasInputs = inputs.length > 0
  const hasOutputs = outputs.length > 0
  if (!hasInputs && !hasOutputs) return null
  return (
    <section className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
      <div
        className={
          hasInputs && hasOutputs
            ? 'grid grid-cols-1 items-start gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center'
            : 'flex flex-col gap-4'
        }
      >
        {hasInputs && <PortColumn label="Inputs" ports={inputs} />}
        {hasInputs && hasOutputs && (
          <div className="flex justify-center text-ink-3 sm:text-ink-4" aria-hidden>
            <ArrowRightIcon size={22} />
          </div>
        )}
        {hasOutputs && <PortColumn label="Outputs" ports={outputs} />}
      </div>
    </section>
  )
}

function PortColumn({ label, ports }: { label: string; ports: string[] }): React.ReactElement {
  return (
    <div>
      <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">{label}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {ports.map((port) => (
          <Badge key={port} variant="outline">
            {port}
          </Badge>
        ))}
      </div>
    </div>
  )
}

function SubprocessFlow({
  processId,
  subprocesses
}: {
  processId: string
  subprocesses: PlantProcess[]
}): React.ReactElement | null {
  const { markFor } = usePlantSessionPaint()
  const processGhosts = useVisibleGhosts(processId, 'Process')
  if (subprocesses.length === 0 && processGhosts.length === 0) return null

  return (
    <section>
      <div className="mb-2 flex items-baseline gap-3">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
          Subprocesses
        </h3>
        <span className="text-xs text-ink-3">{subprocesses.length + processGhosts.length}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        {subprocesses.length > 1 && (
          <UnorderedNotice>
            Subprocess order is not modeled — listed alphabetically.
          </UnorderedNotice>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {subprocesses.map((child) => {
            const mark = markFor(child.id)
            return (
              <PlantHref
                key={child.id}
                id={child.id}
                label="Process"
                node={child}
                className={sessionMarkClass(
                  'inline-flex max-w-65 items-center gap-2 rounded-lg border px-3 py-2 text-left no-underline transition-colors hover:bg-accent hover:text-accent-foreground',
                  mark
                )}
              >
                <span className="grid size-6 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
                  <FlowArrowIcon size={14} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="block truncate text-13 font-semibold text-ink">
                      {child.name}
                    </span>
                    <SessionMarkAffordances mark={mark} />
                  </span>
                  {child.description && (
                    <span className="block truncate text-11 text-ink-3">{child.description}</span>
                  )}
                </span>
              </PlantHref>
            )
          })}
          {processGhosts.map((ghost) => (
            <SessionGhostTile
              key={ghost.id}
              name={ghost.name ?? ghost.id}
              view={ghost.view}
              icon={
                <span className="grid size-6 shrink-0 place-items-center rounded-md bg-st-down/15 text-st-down">
                  <FlowArrowIcon size={14} />
                </span>
              }
            />
          ))}
        </div>
      </div>
    </section>
  )
}

interface ImplementationsBySite {
  siteId: string | '__unsited__'
  siteName: string
  implementations: PlantProcessImplementation[]
}

function ImplementationsGrid({
  processId,
  implementations
}: {
  processId: string
  implementations: PlantProcessImplementation[]
}): React.ReactElement {
  const grouped = React.useMemo(
    () => groupImplementationsBySite(implementations),
    [implementations]
  )
  const lineGhosts = useVisibleGhosts(processId, 'Line')

  return (
    <section>
      <div className="mb-2 flex items-baseline gap-3">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
          Implementations · Sites &amp; Lines
        </h3>
        <span className="text-xs text-ink-3">
          {implementations.length} {implementations.length === 1 ? 'line' : 'lines'} on{' '}
          {grouped.length} {grouped.length === 1 ? 'site' : 'sites'}
        </span>
      </div>
      {implementations.length === 0 && lineGhosts.length === 0 ? (
        <div className="rounded-xl border bg-card p-4 text-sm text-ink-3 shadow-sm">
          Not implemented at any site.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(20rem,1fr))]">
          {grouped.map((group) => (
            <SiteImplementationCard key={group.siteId} group={group} />
          ))}
          {lineGhosts.map((ghost) => (
            <SessionGhostTile
              key={ghost.id}
              name={ghost.name ?? ghost.id}
              view={ghost.view}
              icon={
                <span className="grid size-6 place-items-center rounded-md bg-st-down/15 text-st-down">
                  <FlowArrowIcon size={13} />
                </span>
              }
            />
          ))}
        </div>
      )}
    </section>
  )
}

function SiteImplementationCard({ group }: { group: ImplementationsBySite }): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
      {group.siteId === '__unsited__' ? (
        <div className="mb-2 text-sm font-semibold text-ink-2">{group.siteName}</div>
      ) : (
        <PlantHref
          id={group.siteId}
          label="Site"
          className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-brand no-underline hover:underline"
        >
          {group.siteName}
          <SessionEntityMark entityId={group.siteId} />
          <span className="text-11 font-normal" aria-hidden>
            →
          </span>
        </PlantHref>
      )}
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {group.implementations.map((impl) => (
          <li key={impl.line.id}>
            <ImplementationRow
              impl={impl}
              siteId={group.siteId === '__unsited__' ? undefined : group.siteId}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

function ImplementationRow({
  impl,
  siteId
}: {
  impl: PlantProcessImplementation
  siteId: string | undefined
}): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(impl.line.id)
  const isSubline = impl.sublineParent !== null
  const content = (
    <>
      <span className="grid size-6 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <FlowArrowIcon size={13} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block truncate text-13 font-semibold text-ink">
            {impl.line.name}
            {isSubline && impl.sublineParent && (
              <span className="ml-1 text-11 font-normal text-ink-3">
                · subline of {impl.sublineParent.name}
              </span>
            )}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        {(impl.line.line_code || impl.line.description) && (
          <span className="block truncate text-11 text-ink-3">
            {impl.line.line_code}
            {impl.line.line_code && impl.line.description ? ' · ' : ''}
            {impl.line.description}
          </span>
        )}
      </span>
    </>
  )

  if (!siteId) {
    return (
      <div
        className={sessionMarkClass(
          'flex min-w-0 items-center gap-2 rounded-md border bg-card px-2.5 py-1.5',
          mark
        )}
      >
        {content}
      </div>
    )
  }
  return (
    <PlantHref
      id={impl.line.id}
      label="Line"
      node={{ ...impl.line, siteId }}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 no-underline transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      {content}
    </PlantHref>
  )
}

function collectSites(
  implementations: PlantProcessImplementation[]
): { id: string; name: string }[] {
  const seen = new Map<string, string>()
  for (const impl of implementations) {
    if (!impl.site) continue
    if (!seen.has(impl.site.id)) seen.set(impl.site.id, impl.site.name)
  }
  return Array.from(seen, ([id, name]) => ({ id, name }))
}

function groupImplementationsBySite(
  implementations: PlantProcessImplementation[]
): ImplementationsBySite[] {
  const order: string[] = []
  const groups = new Map<string, ImplementationsBySite>()
  const UNSITED = '__unsited__'
  for (const impl of implementations) {
    const site = impl.site
    const key: string = site ? site.id : UNSITED
    const existing = groups.get(key)
    if (existing) {
      existing.implementations.push(impl)
      continue
    }
    order.push(key)
    groups.set(key, {
      siteId: site ? site.id : UNSITED,
      siteName: site?.name ?? 'Unsited',
      implementations: [impl]
    })
  }
  return order.flatMap((key) => {
    const group = groups.get(key)
    return group ? [group] : []
  })
}

function collectPorts(ports: string[] | undefined): string[] {
  return (ports ?? []).filter((port) => port.trim().length > 0)
}
